import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Compact, content-rich page hero used on every inner page.
 * Accepts optional ctas, stats and meta to keep the left column visually balanced
 * with the right portrait/image.
 */
export default function PageHero({
  eyebrow,
  title,
  sub,
  image,
  ctas = [],     // [{ label, href?, to?, variant: 'primary'|'secondary' }]
  stats = [],    // [{ k, v }]
  meta = [],     // [{ label, value }]
}) {
  return (
    <section className="relative pt-28 md:pt-32 pb-14 md:pb-20 bg-cream overflow-hidden">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-96 w-[60rem] rounded-full bg-taupe/15 blur-3xl" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-bronze/15" />

      <div className="container-page relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-stretch">
        {/* Left column */}
        <div className="lg:col-span-7 flex flex-col">
          {eyebrow && (
            <div className="eyebrow mb-5 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-bronze" /> {eyebrow}
            </div>
          )}
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-ink leading-[1.04] tracking-tight">
            {title}
          </h1>
          {sub && (
            <p className="mt-6 max-w-xl text-base md:text-lg font-light text-ink_soft/80 leading-relaxed">
              {sub}
            </p>
          )}

          {ctas.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-3">
              {ctas.map((c) =>
                c.to ? (
                  <Link
                    key={c.label}
                    to={c.to}
                    data-testid={`pagehero-cta-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={c.variant === "secondary" ? "btn-secondary" : "btn-primary"}
                  >
                    {c.label}
                  </Link>
                ) : (
                  <a
                    key={c.label}
                    href={c.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`pagehero-cta-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={c.variant === "secondary" ? "btn-secondary" : "btn-primary"}
                  >
                    {c.label}
                  </a>
                )
              )}
            </div>
          )}

          {/* Bottom row: stats / meta — fills the vertical space */}
          {(stats.length > 0 || meta.length > 0) && (
            <div className="mt-auto pt-10 md:pt-12 grid grid-cols-2 md:grid-cols-3 gap-6 max-w-xl border-t border-bronze/15">
              {stats.map((s) => (
                <div key={s.k}>
                  <div className="font-serif text-3xl text-ink leading-none">{s.k}</div>
                  <div className="text-[0.65rem] tracking-[0.22em] uppercase text-olive mt-2">
                    {s.v}
                  </div>
                </div>
              ))}
              {meta.map((m) => (
                <div key={m.label}>
                  <div className="text-[0.65rem] tracking-[0.22em] uppercase text-olive">
                    {m.label}
                  </div>
                  <div className="mt-1 font-serif text-xl text-ink">{m.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right column — image */}
        {image && (
          <div className="lg:col-span-5 hidden lg:flex items-stretch">
            <div className="relative w-full bg-beige border-8 border-cream shadow-xl overflow-hidden">
              <img
                src={image}
                alt=""
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-beige/30 to-transparent" />
              <span className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-cream pointer-events-none">
                <span className="text-[0.65rem] tracking-[0.25em] uppercase opacity-80">
                  A Homely Abode
                </span>
                <ArrowRight size={16} className="opacity-70" />
              </span>
            </div>
          </div>
        )}

        {/* Mobile image */}
        {image && (
          <div className="lg:hidden">
            <div className="relative aspect-[4/3] w-full bg-beige border-8 border-cream shadow-xl overflow-hidden">
              <img
                src={image}
                alt=""
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
